import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { textoCorresponde } from '@/features/SI/utils/buscaTexto';
import { SI_PORTAL_BASE_PATH } from '../siPortal.definitions';
import { SiPortalEmBreve } from '../components/SiPortalEmBreve';
import { categoriaDocumento, dataDoFeed, pendenciasDeCiencia } from '../publicacoes/publicacao.definitions';
import { CATALOGO_FILTRO_DOCUMENTOS, filtroEfetivo, opcoesComConteudo } from '../publicacoes/filtrosCategoria';
import { useMeusEstadosQuery, usePublicacoesPublicadasQuery } from '../publicacoes/usePublicacoes';
import { PendenciasCienciaFaixa } from '../publicacoes/components/PendenciasCienciaFaixa';
import { DocumentoCard } from './DocumentoCard';

export const linkDoDocumento = (id: string) => `${SI_PORTAL_BASE_PATH}/documentos/${id}`;

/**
 * Aba Documentos (Fase 4 do Portal do SI): políticas, normas, manuais e termos — material de
 * referência, com versão e ciência. `/si/documentos/:id` (link de pendência/feed) destaca e rola
 * até o documento.
 */
export default function DocumentosPage() {
  const { id: destacadoId } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const agora = useMemo(() => Date.now(), []);
  const publicadas = usePublicacoesPublicadasQuery();
  const documentos = useMemo(
    () => (publicadas.data ?? []).filter((p) => p.tipo === 'documento').sort((a, b) => dataDoFeed(b) - dataDoFeed(a)),
    [publicadas.data]
  );
  const { estados } = useMeusEstadosQuery(documentos);
  const [escolhida, setCategoria] = useState('');
  // Só as categorias com documento publicado; "Todos" só com mais de uma.
  const opcoes = useMemo(
    () => opcoesComConteudo(CATALOGO_FILTRO_DOCUMENTOS, new Set(documentos.map((d) => d.categoria)), 'Todos'),
    [documentos]
  );
  const categoria = filtroEfetivo(escolhida, opcoes);
  const [busca, setBusca] = useState('');

  const pendencias = useMemo(
    () => pendenciasDeCiencia(documentos, estados, user?.collaboratorId, agora),
    [documentos, estados, user?.collaboratorId, agora]
  );
  const exibidos = documentos.filter(
    (d) =>
      (!categoria || d.categoria === categoria) &&
      (!busca.trim() || textoCorresponde([d.titulo, d.resumo, categoriaDocumento(d.categoria).label], busca))
  );

  useEffect(() => {
    if (destacadoId && documentos.some((d) => d.id === destacadoId)) {
      document.getElementById(`documento-${destacadoId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [destacadoId, documentos]);

  return (
    <div className="space-y-5" data-tour="si-documentos">
      <PendenciasCienciaFaixa
        pendencias={pendencias}
        linkDe={(d) => linkDoDocumento(d.id)}
        rotulo={{ um: '1 documento aguardando sua ciência', varios: (n) => `${n} documentos aguardando sua ciência` }}
      />

      {documentos.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-tour="si-doc-filtros">
          <div className="flex flex-wrap gap-1 text-xs font-medium" role="group" aria-label="Filtrar por categoria">
            {opcoes.map((opcao) => (
              <button
                key={opcao.valor || 'todos'}
                type="button"
                aria-pressed={categoria === opcao.valor}
                onClick={() => setCategoria(opcao.valor)}
                className={clsx(
                  'rounded-full px-3 py-1.5 transition-colors',
                  categoria === opcao.valor
                    ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-200'
                    : 'text-raro-gray-600 hover:bg-white hover:text-primary-700'
                )}
              >
                {opcao.label}
              </button>
            ))}
          </div>
          <label className="relative w-full sm:w-64">
            <span className="sr-only">Pesquisar documento</span>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-raro-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Pesquisar documento..."
              className="w-full rounded-full border border-raro-gray-200 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </label>
        </div>
      )}

      {publicadas.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-48 animate-pulse rounded-2xl bg-raro-gray-100" />
          <div className="h-48 animate-pulse rounded-2xl bg-raro-gray-100" />
        </div>
      ) : documentos.length === 0 ? (
        <SiPortalEmBreve
          icon={DocumentTextIcon}
          titulo="Nenhum documento ainda"
          descricao="Políticas, normas, manuais e termos do time de SI vão aparecer aqui."
        />
      ) : exibidos.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-raro-gray-300 bg-white px-4 py-6 text-center text-sm text-raro-gray-500">
          {categoria === 'onboarding' && !busca.trim()
            ? 'Em breve: o material de boas-vindas de Segurança da Informação vai ficar aqui.'
            : 'Nenhum documento com esse filtro.'}
        </p>
      ) : (
        <div className="grid items-stretch gap-4 md:grid-cols-2">
          {exibidos.map((documento) => (
            <DocumentoCard
              key={documento.id}
              documento={documento}
              estado={estados.get(documento.id)}
              colaboradorId={user?.collaboratorId}
              agora={agora}
              destacado={documento.id === destacadoId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
